// CTV-563: client-side ad-spec probe. See docs/adr/0020-client-only-ad-spec-validation.md
// for why this lives only in the browser and skips Library/Archived reuse paths.
//
// Reads <video>.duration, videoWidth/videoHeight, and best-effort audio hints
// (audioTracks / mozHasAudio / webkitAudioDecodedByteCount) off a temporary
// off-DOM HTMLVideoElement.

export const AD_SPEC_LIMITS = {
  durations: [15, 30] as const,
  durationToleranceSec: 1,
  // 16:9 widescreen. We enforce the shape, not the pixel count — a 1280×720
  // spot is welcome, a 1080×1080 square is not. Buyers are still told in-UI
  // to bring a 1920×1080+ master; this validator just keeps sub-1080p
  // renders from getting hard-rejected. Tolerance ±0.02 covers 1918×1080,
  // 1920×1078, and other slightly-off renders while still catching 4:3 /
  // 1:1 / 9:16 mistakes.
  targetAspectRatio: 16 / 9,
  aspectRatioTolerance: 0.02,
};

export type AdSpecViolation =
  | { kind: 'duration'; actualSeconds: number }
  | { kind: 'aspectRatio'; actualWidth: number; actualHeight: number };

export interface AdSpecResult {
  ok: boolean;
  violations: AdSpecViolation[];
  audioDetected: boolean;
}

const METADATA_TIMEOUT_MS = 5000;
const AUDIO_RECHECK_DELAY_MS = 250;

// Rounded to one decimal so a browser reporting 30.0001s doesn't drift out
// of the tolerance band.
function isAllowedDuration(seconds: number): boolean {
  const rounded = Math.round(seconds * 10) / 10;
  const tol = AD_SPEC_LIMITS.durationToleranceSec;
  return AD_SPEC_LIMITS.durations.some((target) => Math.abs(rounded - target) <= tol);
}

interface AudioHintsVideo extends HTMLVideoElement {
  audioTracks?: { length: number };
  mozHasAudio?: boolean;
  webkitAudioDecodedByteCount?: number;
  captureStream?: () => MediaStream;
  mozCaptureStream?: () => MediaStream;
}

// Returns:
//   'yes'      — a positive signal fired (authoritative)
//   'no'       — an authoritative negative signal fired
//   'unknown'  — no signal exposed enough info to decide
//
// The three legacy properties are each single-vendor: audioTracks and
// mozHasAudio are Firefox, webkitAudioDecodedByteCount is Safari-and-needs-
// playback. On Chrome (majority browser) they're all silent for a
// metadata-only preload, which is why this used to false-negative constantly.
// captureStream() is the cross-browser rescue: the returned MediaStream
// mirrors the source's tracks, so an audio track shows up whether the file
// has audio or not, at metadata-load time, in every modern engine.
function detectAudio(v: AudioHintsVideo): 'yes' | 'no' | 'unknown' {
  if (v.audioTracks && v.audioTracks.length > 0) return 'yes';
  if (v.mozHasAudio === true) return 'yes';
  if (typeof v.webkitAudioDecodedByteCount === 'number' && v.webkitAudioDecodedByteCount > 0) return 'yes';

  const captureFn = v.captureStream || v.mozCaptureStream;
  if (typeof captureFn === 'function') {
    try {
      const stream = captureFn.call(v);
      return stream.getAudioTracks().length > 0 ? 'yes' : 'no';
    } catch {
      // captureStream can throw on some engines when metadata isn't ready.
      // Fall through to the legacy negative signals.
    }
  }

  if (v.mozHasAudio === false) return 'no';
  return 'unknown';
}

export async function validateAdSpecs(blob: Blob): Promise<AdSpecResult> {
  const url = URL.createObjectURL(blob);
  const video = document.createElement('video') as AudioHintsVideo;
  video.preload = 'metadata';
  video.muted = true;
  video.src = url;

  try {
    await new Promise<void>((resolve, reject) => {
      const timeout = setTimeout(() => {
        cleanup();
        reject(new Error('metadata-timeout'));
      }, METADATA_TIMEOUT_MS);
      const onLoaded = () => {
        cleanup();
        resolve();
      };
      const onError = () => {
        cleanup();
        reject(new Error('metadata-error'));
      };
      const cleanup = () => {
        clearTimeout(timeout);
        video.removeEventListener('loadedmetadata', onLoaded);
        video.removeEventListener('error', onError);
      };
      video.addEventListener('loadedmetadata', onLoaded);
      video.addEventListener('error', onError);
    }).catch(() => {
      // Corrupt/empty blob → surface as a duration violation rather than
      // hanging the UI. Zero seconds is definitionally out of spec.
      return Promise.reject('timeout');
    });
  } catch {
    URL.revokeObjectURL(url);
    return {
      ok: false,
      violations: [{ kind: 'duration', actualSeconds: 0 }],
      audioDetected: false,
    };
  }

  try {
    const violations: AdSpecViolation[] = [];
    const duration = video.duration;
    if (!Number.isFinite(duration) || duration <= 0 || !isAllowedDuration(duration)) {
      violations.push({ kind: 'duration', actualSeconds: Number.isFinite(duration) ? duration : 0 });
    }

    const width = video.videoWidth;
    const height = video.videoHeight;
    if (width <= 0 || height <= 0) {
      violations.push({ kind: 'aspectRatio', actualWidth: width, actualHeight: height });
    } else {
      const ratio = width / height;
      if (Math.abs(ratio - AD_SPEC_LIMITS.targetAspectRatio) > AD_SPEC_LIMITS.aspectRatioTolerance) {
        violations.push({ kind: 'aspectRatio', actualWidth: width, actualHeight: height });
      }
    }

    // Safari populates webkitAudioDecodedByteCount asynchronously once
    // decoding starts, so give it a beat before deciding "no audio".
    let audioSignal = detectAudio(video);
    if (audioSignal !== 'yes') {
      await new Promise((r) => setTimeout(r, AUDIO_RECHECK_DELAY_MS));
      audioSignal = detectAudio(video);
    }

    // Only surface a "no audio" warning when we have an authoritative
    // negative. 'unknown' means the browser didn't expose enough info to
    // decide, in which case the file may well have audio and warning would
    // be a false alarm.
    return {
      ok: violations.length === 0,
      violations,
      audioDetected: audioSignal !== 'no',
    };
  } finally {
    URL.revokeObjectURL(url);
  }
}

// Human-facing composition of one or more violations into a single sentence.
// One violation → standalone sentence. Two → " and " join, first clause
// dropping its trailing period.
export function formatAdSpecViolations(violations: AdSpecViolation[]): string {
  if (violations.length === 0) return '';
  const parts = violations.map((v) => {
    if (v.kind === 'duration') {
      const shown = Number.isFinite(v.actualSeconds) && v.actualSeconds > 0
        ? `${Math.round(v.actualSeconds * 10) / 10}s`
        : 'unknown';
      return `must be 15 or 30 seconds long (yours is ${shown})`;
    }
    const shownDims = v.actualWidth > 0 && v.actualHeight > 0
      ? `${v.actualWidth}×${v.actualHeight}`
      : 'unknown';
    return `must be a 16:9 widescreen video (yours is ${shownDims})`;
  });
  if (parts.length === 1) {
    return `Commercial ${parts[0]}.`;
  }
  return `Commercial ${parts.join(' and ')}.`;
}
